import type { Device } from 'homey-api';

import { getIsDeviceQuickActionStateOnly } from '../../lib/deviceQuickAction';

import type {
  DeviceQuickActionFamily,
  DeviceQuickActionProps,
  DeviceQuickActionVariant,
} from './DeviceQuickAction.types';
import { DeviceQuickActionButton } from './DeviceQuickActionButton';
import { DeviceQuickActionLightButton } from './DeviceQuickActionLightButton';
import { DeviceQuickActionLightToggle } from './DeviceQuickActionLightToggle';
import { DeviceQuickActionMediaButton } from './DeviceQuickActionMediaButton';
import { DeviceQuickActionMediaToggle } from './DeviceQuickActionMediaToggle';
import { DeviceQuickActionToggle } from './DeviceQuickActionToggle';

export type { DeviceQuickActionProps } from './DeviceQuickAction.types';

const MEDIA_QUICK_ACTION_BASE_IDS = [
  'channel_down',
  'channel_up',
  'speaker_shuffle',
  'speaker_prev',
  'speaker_playing',
  'speaker_next',
  'speaker_repeat',
  'volume_down',
  'volume_mute',
  'volume_up',
];

interface DeviceQuickActionCapability {
  getable?: boolean;
  id: string;
  setable?: boolean;
  type?: string;
}

export function DeviceQuickAction(props: DeviceQuickActionProps) {
  const family = getDeviceQuickActionFamily({
    baseId: props.baseId,
    device: props.device,
  });

  if (family === 'light') {
    if (props.variant === 'toggle') {
      return <DeviceQuickActionLightToggle {...props} />;
    }

    return <DeviceQuickActionLightButton {...props} />;
  }

  if (family === 'media') {
    if (props.variant === 'toggle') {
      return <DeviceQuickActionMediaToggle {...props} />;
    }

    return <DeviceQuickActionMediaButton {...props} />;
  }

  if (props.variant === 'toggle') {
    return <DeviceQuickActionToggle {...props} />;
  }

  return <DeviceQuickActionButton {...props} />;
}

export function getDeviceQuickActionVariant(props: {
  capabilityObj: DeviceQuickActionCapability | null | undefined;
  device: Device | null | undefined;
}): DeviceQuickActionVariant | null {
  if (props.capabilityObj == null) {
    return null;
  }

  if (props.capabilityObj.getable === true) {
    if (props.capabilityObj.type !== 'boolean') {
      return null;
    }

    if (
      getIsDeviceQuickActionStateOnly({
        capabilityObj: props.capabilityObj,
        device: props.device,
      }) === true
    ) {
      return null;
    }

    return 'toggle';
  }

  if (props.capabilityObj.setable !== true) {
    return null;
  }

  return 'button';
}

export function shouldRenderDeviceQuickAction(props: {
  capabilityObj: DeviceQuickActionCapability | null | undefined;
  device: Device | null | undefined;
  isLocked: boolean;
  variant: DeviceQuickActionVariant | null | undefined;
}): boolean {
  if (props.variant == null || props.capabilityObj == null || props.device == null) {
    return false;
  }

  if (props.isLocked === true) {
    return false;
  }

  if (props.device.ready !== true || props.device.available === false) {
    return false;
  }

  return true;
}

function getDeviceQuickActionFamily(props: {
  baseId: string;
  device: Device | null | undefined;
}): DeviceQuickActionFamily {
  if (isDeviceQuickActionLight(props) === true) {
    return 'light';
  }

  if (isDeviceQuickActionMedia(props) === true) {
    return 'media';
  }

  return 'default';
}

function isDeviceQuickActionLight(props: {
  baseId: string;
  device: Device | null | undefined;
}): boolean {
  return (
    props.baseId === 'onoff' &&
    (props.device?.virtualClass === 'light' || props.device?.class === 'light')
  );
}

function isDeviceQuickActionMedia(props: { baseId: string }): boolean {
  return MEDIA_QUICK_ACTION_BASE_IDS.includes(props.baseId);
}
